import { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Shield, ShieldCheck, ShieldAlert, AlertTriangle, CheckCircle, XCircle, TrendingUp, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const getBackendUrl = () => {
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();

function SecurityStatus() {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/nist-security/my`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      }
    } catch (error) {
      console.error('Failed to load security status');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (sectionName) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  const getGradeColor = (grade) => {
    if (!grade) return 'text-muted-foreground';
    if (grade.startsWith('A')) return 'text-primary';
    if (grade.startsWith('B')) return 'text-accent';
    if (grade.startsWith('C')) return 'text-yellow-500';
    return 'text-destructive';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-primary';
    if (score >= 60) return 'text-accent';
    if (score >= 40) return 'text-yellow-500';
    return 'text-destructive';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-primary/10 border-primary/20';
    if (score >= 60) return 'bg-accent/10 border-accent/20';
    if (score >= 40) return 'bg-yellow-500/10 border-yellow-500/20';
    return 'bg-destructive/10 border-destructive/20';
  };

  const getRiskLevelColor = (level) => {
    const l = (level || '').toLowerCase();
    if (l === 'low') return 'bg-primary/10 text-primary border-primary/20';
    if (l === 'medium') return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
    if (l === 'high') return 'bg-destructive/10 text-destructive border-destructive/20';
    return 'bg-muted/10 text-muted-foreground border-muted/20';
  };

  const getSectionIcon = (name) => {
    switch (name) {
      case 'Identify': return '🔍';
      case 'Protect': return '🛡️';
      case 'Detect & Respond': return '⚡';
      case 'Recover': return '🔄';
      default: return '📋';
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Security Status (NIST)">
        <div className="flex items-center justify-center h-64">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!status) {
    return (
      <DashboardLayout title="Security Status (NIST)">
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Shield className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-xl font-mono font-bold mb-2">No Security Assessment</h3>
          <p className="text-muted-foreground">
            Your NIST security assessment hasn't been uploaded yet.<br />
            Contact your administrator to request an assessment.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Security Status (NIST)">
      <div className="space-y-6">
        {/* Header with Overall Score */}
        <div className="bg-gradient-to-r from-primary/10 to-accent/5 border border-primary/20 rounded-lg p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="h-20 w-20 rounded-full bg-background border-4 border-primary flex items-center justify-center">
                <span className={`text-3xl font-mono font-bold ${getGradeColor(status.grade)}`}>
                  {status.grade}
                </span>
              </div>
              <div>
                <h2 className="text-2xl font-mono font-bold mb-1">Security Status (NIST)</h2>
                <p className="text-muted-foreground">
                  Based on NIST Cybersecurity Framework Assessment
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Last updated: {new Date(status.last_updated).toLocaleDateString()}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground font-mono">Overall Score</p>
              <p className={`text-5xl font-mono font-bold ${getScoreColor(status.overall_score)}`}>
                {status.overall_score}%
              </p>
            </div>
          </div>
        </div>

        {/* Risk Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-lg p-4 text-center">
            <p className="text-xs text-muted-foreground font-mono mb-1">Total Risks</p>
            <p className="text-2xl font-mono font-bold">{status.risk_summary?.total || 0}</p>
          </div>
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 text-center">
            <p className="text-xs text-muted-foreground font-mono mb-1">Mitigated</p>
            <p className="text-2xl font-mono font-bold text-primary">{status.risk_summary?.mitigated || 0}</p>
          </div>
          <div className="bg-accent/5 border border-accent/20 rounded-lg p-4 text-center">
            <p className="text-xs text-muted-foreground font-mono mb-1">Escalated</p>
            <p className="text-2xl font-mono font-bold text-accent">{status.risk_summary?.escalate || 0}</p>
          </div>
          <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-4 text-center">
            <p className="text-xs text-muted-foreground font-mono mb-1">High Risk</p>
            <p className="text-2xl font-mono font-bold text-destructive">{status.risk_summary?.high || 0}</p>
          </div>
        </div>

        {/* NIST Sections */}
        <div className="space-y-4">
          <h3 className="text-lg font-mono font-bold">NIST Framework Breakdown</h3>
          
          {/* Section Score Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {status.sections?.map((section, idx) => (
              <div 
                key={idx} 
                className={`border rounded-lg p-4 ${getScoreBg(section.score)}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">{getSectionIcon(section.name)}</span>
                  <span className="font-mono font-bold text-sm">{section.name}</span>
                </div>
                <p className={`text-3xl font-mono font-bold ${getScoreColor(section.score)}`}>
                  {section.score}%
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {section.mitigated_risks}/{section.total_risks} mitigated
                </p>
              </div>
            ))}
          </div>

          {/* Detailed Sections */}
          {status.sections?.map((section, idx) => (
            <div key={idx} className="bg-card border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection(section.name)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-muted/5 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{getSectionIcon(section.name)}</span>
                  <div className="text-left">
                    <h4 className="font-mono font-bold">{section.name}</h4>
                    <p className="text-xs text-muted-foreground">
                      {section.total_risks} risks assessed • {section.mitigated_risks} mitigated
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`text-xl font-mono font-bold ${getScoreColor(section.score)}`}>
                    {section.score}%
                  </span>
                  {expandedSections[section.name] ? (
                    <ChevronUp className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
              </button>
              
              {expandedSections[section.name] && (
                <div className="border-t border-border divide-y divide-border">
                  {section.risks?.map((risk, rIdx) => (
                    <div key={rIdx} className="p-4 hover:bg-muted/5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {risk.response?.toLowerCase() === 'yes' ? (
                              <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                            ) : (
                              <XCircle className="h-4 w-4 text-destructive flex-shrink-0" />
                            )}
                            <p className="text-sm font-mono">{risk.name}</p>
                          </div>
                          {risk.commentary && (
                            <p className="text-xs text-muted-foreground ml-6 mb-2">
                              {risk.commentary}
                            </p>
                          )}
                          {risk.recommendations && risk.recommendations !== '0' && (
                            <div className="ml-6 mt-2 p-2 bg-accent/5 border border-accent/20 rounded text-xs">
                              <span className="font-bold text-accent">Recommendation:</span>{' '}
                              {risk.recommendations}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-mono border ${getRiskLevelColor(risk.risk_level)}`}>
                            {risk.risk_level || 'N/A'}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs font-mono ${
                            risk.risk_response?.toLowerCase() === 'mitigated' 
                              ? 'bg-primary/10 text-primary' 
                              : 'bg-accent/10 text-accent'
                          }`}>
                            {risk.risk_response || 'Pending'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* File Info */}
        {status.file_name && (
          <div className="text-center text-xs text-muted-foreground">
            <FileText className="h-4 w-4 inline mr-1" />
            Assessment file: {status.file_name}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default SecurityStatus;
